import { AuthStatus } from "./auth_client";

// auth_messages — the shared, HEADLESS statusCode → { text, tone } catalogue for
// AuthClient results (plans/2026-07-11--shared-auth-client-otp-refactor.md).
//
// Three self-owned hosts each writing copy for ~12 statusCodes would drift
// immediately (three subtly different "that code wasn't right" strings, three
// offline messages). This single-sources the WORDS and the TONE semantics while
// staying DOM-free — it is data beside the client, so it cannot reintroduce the
// shared-widget coupling. Each host renders its OWN status element from its state
// machine (authStatus = { code, tone, text }); it pulls copy from here, and may
// pass per-surface overrides where the surface warrants it (e.g. the Rankometer
// suppresses CODE_SENT because its own "Code sent to <email>" heading already says
// it; the password panel points TOO_MANY_ATTEMPTS at the emailed-code escape hatch).
//
// Tones: info | error | busy. `busy` is for the transient "…in progress" phases a
// host sets while awaiting the client; the rest map AuthClient result statusCodes.

// An entry's `text` is either a string or a function (ctx) => string, so a message
// can interpolate host context (e.g. the emailed address in CODE_SENT).
export const AuthMessages = {
  // --- requestCode results ---
  [AuthStatus.CODE_SENT]: {
    tone: "info",
    text: (ctx) =>
      ctx && ctx.email
        ? `We've emailed a code to ${ctx.email}. It expires in 10 minutes.`
        : "We've emailed you a code. It expires in 10 minutes.",
  },
  [AuthStatus.HAS_USER_ACCOUNT]: {
    tone: "info",
    text: "This email has a password. Enter it to sign in.",
  },
  [AuthStatus.THROTTLED]: {
    tone: "info",
    text: "A code was already sent to this address recently. Enter it, or wait a moment to resend.",
  },
  [AuthStatus.INVALID_EMAIL]: {
    tone: "error",
    text: "That doesn't look like a valid email address. Please check and try again.",
  },

  // --- verifyCode / verifyPassword results ---
  [AuthStatus.AUTHENTICATED]: { tone: "info", text: "You're signed in." },
  [AuthStatus.INVALID_CODE]: {
    tone: "error",
    text: "That code wasn't right. Please check it and try again.",
  },
  [AuthStatus.EXPIRED_CODE]: {
    tone: "error",
    text: "That code has expired. Request a fresh one.",
  },
  // Default TOO_MANY_ATTEMPTS copy is the CODE path (the primary flow). The
  // password panel overrides it to point at the emailed-code escape hatch — the
  // code path is unaffected by the password attempt cap.
  [AuthStatus.TOO_MANY_ATTEMPTS]: {
    tone: "error",
    text: "Too many attempts. Request a fresh code.",
  },
  [AuthStatus.INVALID_CREDENTIALS]: {
    tone: "error",
    text: "That password wasn't right. Please check it and try again.",
  },

  // --- logout result ---
  [AuthStatus.LOGGED_OUT]: { tone: "info", text: "You're signed out." },

  // --- requestPasswordReset result (enumeration-uniform copy — says nothing about
  // whether the address has an account) ---
  [AuthStatus.RESET_SENT]: {
    tone: "info",
    text: "If that address has a Rankers account, we've emailed a password-reset link.",
  },

  // --- transport / fallback ---
  [AuthStatus.NETWORK_ERROR]: {
    tone: "error",
    text: "You're offline — check your connection and try again.",
  },
  [AuthStatus.UNKNOWN_ERROR]: {
    tone: "error",
    text: "Something went wrong. Please try again.",
  },

  // --- transient busy phases (host-set while awaiting the client) ---
  SENDING: { tone: "busy", text: "Emailing your code…" },
  SENDING_RESET: { tone: "busy", text: "Emailing your reset link…" },
  CHECKING: { tone: "busy", text: "Checking your code…" },
  SIGNING_IN: { tone: "busy", text: "Signing you in…" },
  SIGNING_OUT: { tone: "busy", text: "Signing you out…" },
};

// A ready-made override for password panels: the attempt cap is a password-only
// concern, and the emailed code remains the primary, un-capped escape hatch.
export const PASSWORD_PANEL_OVERRIDES = {
  [AuthStatus.TOO_MANY_ATTEMPTS]: {
    tone: "error",
    text: "Too many password attempts. Wait a few minutes, or use the emailed code instead.",
  },
};

// resolveMessage(code, overrides?, ctx?) → { code, tone, text }
//
// Looks up the catalogue entry for `code` (falling back to UNKNOWN_ERROR so no
// statusCode ever renders an empty status), applies any per-host override, and
// resolves a function `text` against `ctx`. The returned shape is exactly the
// host state machine's `authStatus`.
export function resolveMessage(code, overrides = {}, ctx = {}) {
  const base = AuthMessages[code] || AuthMessages[AuthStatus.UNKNOWN_ERROR];
  const override = overrides && overrides[code];
  const entry = override ? { ...base, ...override } : base;
  const text = typeof entry.text === "function" ? entry.text(ctx) : entry.text;
  return { code: code, tone: entry.tone, text: text };
}
